import React, { ReactNode, useEffect, useState } from "react";
import { Dialog, DialogActions, DialogContent, DialogTitle, Slide, Typography, TextField, FormControl, FormLabel, RadioGroup, FormControlLabel, Radio, Box, Button } from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import { Fonts } from "../types/Constants";
import { useDispatch } from "react-redux";
import { fetchError, showMessage } from "../redux/actions";
import IosShareIcon from '@mui/icons-material/IosShare';

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement;
  },
  // eslint-disable-next-line no-undef
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

interface ShareGroupDialogProps {
  dialogTitle: string | ReactNode;
  open: boolean;
  onDeny?: (isOpen: boolean) => void;
  title: string | ReactNode;
  content: string | ReactNode;
  onConfirm: (email: string | null, accessLevel: string) => void;
  confirmButtonTitle?: string;
  denyButtonTitle?: string;
  disableDenyButton?: boolean;
  shareCode: string;
}

const ShareGroupDialog: React.FC<ShareGroupDialogProps> = ({
  open,
  onDeny,
  onConfirm,
  title,
  content,
  confirmButtonTitle = "Yes",
  denyButtonTitle = "No",
  disableDenyButton = false,
  shareCode
}) => {

  const dispatch = useDispatch();

  const [emailInput, setEmailInput] = useState<string>("");
  const [radioValue, setRadioValue] = useState<string>("read");
  const [disableButton, setDisableButton] = useState<boolean>(false);

  const link = `https://${window.location.host}/acceptshare?code=${shareCode}`; 

  useEffect(() => {
    if (shareCode !== "") {
      setDisableButton(true);
    }
    else {
      setDisableButton(false);
    }
  }, [shareCode]);

  const handleValueEdited = (event: any) => {
    setEmailInput(event.target.value);
  };

  const handleEnterText = () => {
    onConfirm(emailInput, radioValue);
  };

  const handleChange = (event: any) => {
    setRadioValue(event.target.value);
  };

  const copyLink = () => {
    navigator.clipboard.writeText(link).then(() => {
      dispatch(showMessage("Link copied to clipboard"));
    }).catch((error) => {
      dispatch(fetchError("Failed to copy link: " + error));
    });
  };

  const createMailToLink = (email: string, link: string) => {
    const subject = "You've been invited to a Skidattl Experience!";
    const body = `Oh, hey!

You've been invited to collaborate on a Skidattl Experience in Beacon Blender. Use this link to log in to Beacon Blender and view the Experience.
  
${link}
  
Let's Skidattl!
`;
    return `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  return (
    <Dialog
      TransitionComponent={Transition}
      open={open}
      onClose={() => onDeny && onDeny(false)}
    >
      <DialogTitle>
        <Typography
          sx={{
            mb: 3,
            fontWeight: Fonts.SEMI_BOLD,
          }}
          id="alert-dialog-title"
        >
          {title}
        </Typography>
      </DialogTitle>
      <DialogContent
        sx={{ color: "text.secondary", fontSize: 14 }}
        id="alert-dialog-description"
      >
        <strong>{content}</strong>
        <TextField
          autoFocus
          margin="dense"
          id="value"
          label="Email Address"
          onChange={handleValueEdited}
          type="text"
          fullWidth
          variant="standard"
        />
        <FormControl sx={{ marginTop: 4 }}>
          <RadioGroup
            aria-labelledby="radio-label"
            name="radio-button-group"
            row
            value={radioValue}
            onChange={handleChange}
          >
            <FormControlLabel value="read" control={<Radio size="small" />} label="Viewer" />
            <FormControlLabel value="readwrite" control={<Radio size="small" />} label="Editor" />
            <FormControlLabel value="admin" control={<Radio size="small" />} label="Admin" />
          </RadioGroup>
        </FormControl>
        {radioValue === "read" && (
          <Typography sx={{ fontSize: "10px"}}>Can view the Experience.</Typography>
        )}
        {radioValue === "readwrite" && (
          <Typography sx={{ fontSize: "10px"}}>Can edit the Experience. </Typography>
        )}
        {radioValue === "admin" && (
          <Typography sx={{ fontSize: "10px"}}>Can edit and share the Experience.</Typography>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          pb: 5,
          px: 6,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "end",
        }}
      >
       <Box>
        {shareCode && (
          <>
          <Typography sx={{ marginTop: 2, fontSize: 14 }}>
            Share Code:{" "}
            <a
              href={`https://${window.location.host}/acceptshare?code=${shareCode}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: 'none' }}
            >
              <strong>{`https://${window.location.host}/acceptshare?code=${shareCode}`}</strong>
            </a>
          </Typography>
          <Button
          variant="outlined"
          sx={{
            fontWeight: Fonts.MEDIUM,
            mt: 2
          }}
          onClick={copyLink}
          color="primary"
          autoFocus
        >
          Copy
        </Button>
        </>
        )}
        </Box>
        <Box display="flex">
        {!disableDenyButton &&
          <Button
            variant="outlined"
            sx={{
              fontWeight: Fonts.MEDIUM,
              mr: 2
            }}
            onClick={() => onDeny && onDeny(false)}
            color="secondary"
          >
            {disableButton ? "Close" : denyButtonTitle}
          </Button>
        }
        {!disableButton ? (
          <Button
            variant="contained"
            sx={{
              fontWeight: Fonts.MEDIUM,
            }}
            onClick={handleEnterText}
            color="primary"
            autoFocus
          >
            {confirmButtonTitle}
          </Button>
        ) : (
        <>
        <Button
          variant="contained"
          sx={{
            fontWeight: Fonts.MEDIUM,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
          }}
          onClick={() => window.location.href = createMailToLink(emailInput, link)}
          color="primary"
          autoFocus
        >
          <IosShareIcon sx={{ fontSize: 18 }}/>
          Share
        </Button>
        </>
        )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default ShareGroupDialog;
